import { useEffect, useState } from 'react';
import { Building2, Sparkles, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useCsvImportProgress } from '@/contexts/CsvImportProgressContext';
import { useCsvImportProgressPoll } from '@/hooks/useCsvImportProgressPoll';
import { CSVImportConflictReviewDialog } from '@/components/crm/csv/CSVImportConflictReviewDialog';

import { IconButton } from '@/components/ui/icon-button';
/**
 * Floating pill showing background CSV import progress. Lives in the app
 * shell so it survives page navigation — the user can close the import
 * dialog and still see store progress, then scoring/enrichment for contacts.
 *
 * When a company store finishes with rows that need review, the pill stays
 * until the user reviews or dismisses — with a one-click Review button.
 */
export function CsvImportProgressPill() {
  const { activeImport, setActiveImport } = useCsvImportProgress();
  const progress = useCsvImportProgressPoll(activeImport);
  const [autoDismissedDone, setAutoDismissedDone] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Store finished with rows held for identity review. Do NOT require empty
  // contactIds — scoring may already have started after a people_companies import.
  const storeFailedButDone =
    !!activeImport &&
    progress.isStoreDone &&
    progress.storeFailed > 0 &&
    progress.hasStorePhase &&
    !!activeImport.storeJobId;

  // Companies, people+companies, and large contact stores all surface company
  // identity conflicts through the same review dialog.
  const needsReview = storeFailedButDone;

  useEffect(() => {
    if (!activeImport) return;
    if (!progress.isDone) return;
    if (autoDismissedDone) return;
    // Keep the pill up when conflicts need action — do not auto-dismiss.
    if (needsReview) return;
    setAutoDismissedDone(true);
    const t = setTimeout(() => setActiveImport(null), 8000);
    return () => clearTimeout(t);
  }, [activeImport, progress.isDone, autoDismissedDone, setActiveImport, needsReview]);

  useEffect(() => {
    setAutoDismissedDone(false);
    setReviewOpen(false);
  }, [activeImport?.startedAt, activeImport?.storeJobId]);

  if (!activeImport) return null;

  const {
    storeProcessed,
    storeTotal,
    storeFailed,
    isStoreDone,
    hasStorePhase,
    total,
    scored,
    enriched,
    enrichTarget,
    isScored,
    isDone,
  } = progress;

  const entityLabel =
    activeImport.entityType === 'companies'
      ? 'companies'
      : activeImport.entityType === 'deals'
        ? 'deals'
        : activeImport.entityType === 'pending_contacts'
          ? 'pending contacts'
          : 'contacts';

  const storePct =
    storeTotal > 0 ? Math.min(100, Math.round((storeProcessed / storeTotal) * 100)) : 0;
  const scoringPct = total > 0 ? Math.min(100, Math.round((scored / total) * 100)) : 0;
  const enrichPct = enrichTarget > 0 ? Math.min(100, Math.round((enriched / enrichTarget) * 100)) : 0;

  let phaseLabel: string;
  if (needsReview) {
    phaseLabel = `Import finished — ${storeFailed.toLocaleString()} need review`;
  } else if (isDone) {
    if (hasStorePhase && activeImport.contactIds.length === 0) {
      phaseLabel = `${entityLabel.charAt(0).toUpperCase()}${entityLabel.slice(1)} import complete`;
    } else {
      phaseLabel = 'Best fits ready in your rhythm game';
    }
  } else if (hasStorePhase && !isStoreDone) {
    phaseLabel = `Saving ${storeProcessed.toLocaleString()} of ${storeTotal.toLocaleString()} ${entityLabel}…`;
  } else if (isScored) {
    phaseLabel =
      enrichTarget > 0
        ? `Filling profile details for the top ${enrichTarget} best-fit contacts...`
        : 'No LinkedIn profiles available to fill; wrapping up...';
  } else {
    phaseLabel = `Scoring ${scored.toLocaleString()} of ${total.toLocaleString()} contacts...`;
  }

  const subLine = needsReview
    ? 'Same company name, different website. Press the button below — or open Import History → Review.'
    : hasStorePhase && !isStoreDone
      ? 'Safe to leave this page — import continues in the background.'
      : activeImport.icpNames.length > 0
        ? `Against your top ${activeImport.icpNames.length} ICP${activeImport.icpNames.length === 1 ? '' : 's'}: ${activeImport.icpNames.join(' & ')}`
        : hasStorePhase && isStoreDone && activeImport.contactIds.length === 0
          ? activeImport.label
            ? activeImport.label
            : 'All rows saved.'
          : 'Against your top ICPs';

  const showStoreBar = hasStorePhase && !isStoreDone;
  const showScoringBar = !showStoreBar && !isDone && activeImport.contactIds.length > 0 && !isScored;
  const showEnrichBar = !showStoreBar && !isDone && activeImport.contactIds.length > 0 && isScored;

  const Icon = isDone
    ? storeFailedButDone
      ? AlertTriangle
      : CheckCircle2
    : hasStorePhase && !isStoreDone
      ? Building2
      : Sparkles;

  return (
    <>
      <div
        className={cn(
          'fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)]',
          'rounded-lg border border-border bg-card shadow-lg',
          'p-3 flex flex-col gap-2',
        )}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-2">
          <Icon
            className={cn(
              'h-4 w-4 mt-0.5 shrink-0',
              isDone
                ? storeFailedButDone
                  ? 'text-warning-ink'
                  : 'text-success-ink'
                : 'text-action animate-pulse',
            )}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">{phaseLabel}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{subLine}</p>
          </div>
          <IconButton
            variant="ghost"
            className="h-6 w-6 -mt-1 -mr-1 shrink-0"
            onClick={() => setActiveImport(null)}
            icon={X}
            ariaLabel="Dismiss import progress"
            iconClassName="h-3.5 w-3.5"
          />
        </div>

        {needsReview && activeImport.storeJobId ? (
          <Button
            type="button"
            size="default"
            className="w-full font-semibold"
            onClick={() => setReviewOpen(true)}
          >
            Review {storeFailed.toLocaleString()} conflicts
          </Button>
        ) : null}

        {!isDone && (
          <div className="space-y-1.5">
            {showStoreBar && (
              <div>
                <Progress value={storePct} className="h-1.5" />
                <p className="text-caption text-muted-foreground mt-1 tracking-wide uppercase">
                  Saving rows · {storePct}%
                </p>
              </div>
            )}
            {showScoringBar && (
              <div>
                <Progress value={scoringPct} className="h-1.5" />
                <p className="text-caption text-muted-foreground mt-1 tracking-wide uppercase">
                  Step 1 of 2 · ICP scoring
                </p>
              </div>
            )}
            {showEnrichBar && (
              <div>
                <Progress value={enrichPct} className="h-1.5" />
                <p className="text-caption text-muted-foreground mt-1 tracking-wide uppercase">
                  Step 2 of 2 · Filling best-fit profiles
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {reviewOpen && activeImport.storeJobId ? (
        <CSVImportConflictReviewDialog
          jobId={activeImport.storeJobId}
          workspaceId={activeImport.workspaceId ?? null}
          open
          onOpenChange={setReviewOpen}
          onAllResolved={() => {
            setActiveImport(null);
          }}
        />
      ) : null}
    </>
  );
}
